import * as THREE from 'three';
import { InstancedMesh2 } from '@three.ez/instanced-mesh';

/**
 * Car shadows drawn as InstancedMesh2 batches (docs/PERF.md, "InstancedMesh2 spike"), one per body geometry,
 * instead of one shadow-pass draw per car. The sun's shadow camera covers ±78 m round the view, so in a busy
 * street 60+ car bodies (traffic and parked cars, most of them off screen) each cost a shadow draw: a third of
 * MEDIUM's draw calls. Here each rig keeps one instance (its body pose) in the batch for its mid body; the batch
 * culls the instances per shadow camera and swaps a car to its far impostor beyond the tier's far distance
 * (InstancedMesh2's shadow LOD), so a whole street of shadows is a handful of draws.
 *
 * The batches are shadow-only (`shadowFrustumOnly`). The rig's own body stops casting while it has a slot. What the shadow
 * leaves out: dents (the depth pass never saw the bonnet/boot hinges either), and the full HIGH sculpt near the
 * camera (its mid body casts instead: the same silhouette).
 *
 * InstancedMesh2 draws through an instance-index attribute and a matrices texture (WebGL2 vertex texture
 * fetch), not BatchedMesh / WEBGL_multi_draw, so it runs on iOS Safari.
 *
 * `?im2` / `?noim2` force it on / off (A/B; `shadowBatch.on` is read when a car's rig is built).
 */
const DEFAULT_ON = true;
const query = typeof location !== 'undefined' ? new URLSearchParams(location.search) : null;
export const shadowBatch = {
  on: query?.has('noim2') ? false : query?.has('im2') ? true : DEFAULT_ON,
  /** Beyond this (m) from the camera a car's shadow is its far impostor (the rig's own far distance). */
  farDist: 130,
};

interface Batch {
  mesh: InstancedMesh2;
  farDist: number;
  /** Frames in a row with no instance (an empty batch still costs CPU per pass: dropped after a while). */
  idle: number;
}
/** An empty batch is disposed after this many frames (a body style that left the street, or a pre-Meshy body). */
const IDLE_FRAMES = 300;

let parent: THREE.Object3D | null = null;
let renderer: THREE.WebGLRenderer | null = null;
const batches = new Map<THREE.BufferGeometry, Batch>();
/** Position-only views of the car geometries (shared buffers): InstancedMesh2 adds its own index attribute. */
const shadowGeos = new WeakMap<THREE.BufferGeometry, THREE.BufferGeometry>();
/** Never drawn (main pass) but needed as the batch's material; the shadow pass uses three's depth material. */
let material: THREE.MeshBasicMaterial | null = null;

function shadowGeometry(g: THREE.BufferGeometry): THREE.BufferGeometry {
  let s = shadowGeos.get(g);
  if (!s) {
    s = new THREE.BufferGeometry();
    s.setAttribute('position', g.getAttribute('position'));
    if (g.index) s.setIndex(g.index);
    if (!g.boundingSphere) g.computeBoundingSphere();
    s.boundingSphere = g.boundingSphere!.clone();
    s.name = `${g.name || 'car'}-shadow`;
    shadowGeos.set(g, s);
  }
  return s;
}

/**
 * Shadow-only: three.js asks each object whether it is in a frustum, once for the game camera's render list and
 * once per shadow map. The sun's shadow camera is orthographic (its side planes come in opposite pairs), the
 * game camera a perspective one, so the batches answer yes only to an orthographic frustum: the main pass never
 * even visits them (no culling, no program, no draw), and InstancedMesh2 culls the instances per shadow camera.
 */
function shadowFrustumOnly(f: THREE.Frustum): boolean {
  const p = f.planes;
  return p[0]!.normal.dot(p[1]!.normal) < -0.999 && p[2]!.normal.dot(p[3]!.normal) < -0.999;
}

function batchFor(near: THREE.BufferGeometry, far: THREE.BufferGeometry): Batch {
  let b = batches.get(near);
  if (!b) {
    material ??= new THREE.MeshBasicMaterial();
    const mesh = new InstancedMesh2(shadowGeometry(near), material, { capacity: 32, ...(renderer ? { renderer } : {}) });
    mesh.name = 'car-shadows';
    mesh.castShadow = true;
    mesh.receiveShadow = false;
    // Shadow LOD: the mid body near the camera, the impostor beyond the far distance.
    mesh.addShadowLOD(mesh.geometry);
    mesh.addShadowLOD(shadowGeometry(far), shadowBatch.farDist);
    for (const o of [mesh, ...mesh.children] as THREE.Mesh[]) {
      o.frustumCulled = true;
      o.intersectsFrustum = shadowFrustumOnly;
    }
    b = { mesh, farDist: shadowBatch.farDist, idle: 0 };
    batches.set(near, b);
    parent?.add(mesh);
  }
  return b;
}

/** Vehicle view: the batches live in this scene (the city); `r` lets InstancedMesh2 allocate its buffers at once. */
export function attachShadowBatches(scene: THREE.Object3D, r: THREE.WebGLRenderer | null): void {
  parent = scene;
  renderer = r;
  for (const b of batches.values()) scene.add(b.mesh);
}

export function detachShadowBatches(): void {
  for (const b of batches.values()) {
    b.mesh.removeFromParent();
    b.mesh.dispose();
  }
  batches.clear();
  parent = null;
}

/** Once per frame: the far distance follows the detail tier (the rigs' own impostor distance); idle batches go. */
export function syncShadowBatches(farDist: number): void {
  shadowBatch.farDist = farDist;
  for (const [key, b] of batches) {
    b.idle = b.mesh.instancesCount > 0 ? 0 : b.idle + 1;
    if (b.idle > IDLE_FRAMES) {
      b.mesh.removeFromParent();
      b.mesh.dispose();
      batches.delete(key);
      continue;
    }
    if (b.farDist === farDist) continue;
    b.farDist = farDist;
    b.mesh.updateShadowLOD(1, farDist);
  }
}

export interface ShadowSlot {
  near: THREE.BufferGeometry;
  batch: Batch;
  id: number;
  shown: boolean;
}

/** A car's shadow instance for this body (hidden until `setShadowMatrix`). */
export function allocShadow(near: THREE.BufferGeometry, far: THREE.BufferGeometry): ShadowSlot {
  const batch = batchFor(near, far);
  let id = -1;
  batch.mesh.addInstances(1, (_e, i) => {
    id = i;
  });
  batch.mesh.setVisibilityAt(id, false);
  return { near, batch, id, shown: false };
}

/** The body's world matrix, or `null` to hide the shadow (studio, despawn). */
export function setShadowMatrix(s: ShadowSlot, m: THREE.Matrix4 | null): void {
  if (!m) {
    if (s.shown) s.batch.mesh.setVisibilityAt(s.id, false);
    s.shown = false;
    return;
  }
  s.batch.mesh.setMatrixAt(s.id, m);
  if (!s.shown) s.batch.mesh.setVisibilityAt(s.id, true);
  s.shown = true;
}

export function freeShadow(s: ShadowSlot): void {
  s.batch.mesh.removeInstances(s.id);
  s.shown = false;
}

/** Read-out for tests: instances held per batch. */
export function shadowBatchStats(): { batches: number; instances: number } {
  let instances = 0;
  for (const b of batches.values()) instances += b.mesh.instancesCount;
  return { batches: batches.size, instances };
}
